import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { ColumnField } from "../../../components/GenericTable/SubComponents/Column";

// Base columns without price for normal users
const baseLogoColumnsNormal: ColumnField[] = [
  {
    value: "thumbnail",
    canSortBy: false,
    displayValue: "Image",
    weight: 1.5,
  },
  {
    value: "product.sku",
    canSortBy: true,
    displayValue: "NO.",
    weight: 1,
  },
  {
    value: nameOf<BaseLogo>((x) => x.title),
    canSortBy: true,
    displayValue: "Title",
    weight: 1.5,
  },
  {
    value: "colors",
    canSortBy: false,
    displayValue: "Colors",
    weight: 1.8,
  },
];

// Base columns without price for Admin users
const baseLogoColumnsAdmin: ColumnField[] = [
  {
    value: "thumbnail",
    canSortBy: false,
    displayValue: "Image",
    weight: 1,
  },
  {
    value: "product.sku",
    canSortBy: true,
    displayValue: "NO.",
    weight: 0.8,
  },
  {
    value: nameOf<BaseLogo>((x) => x.title),
    canSortBy: true,
    displayValue: "Title",
    weight: 1.5,
  },
  {
    value: "colors",
    canSortBy: false,
    displayValue: "Colors",
    weight: 1.8,
  },
];

// Customer column (separated for flexible vendor insertion)
const customerColumnAdmin: ColumnField = {
  value: nameOf<BaseLogo>((x) => x.customer),
  canSortBy: true,
  displayValue: "Customer",
  weight: 1,
};
const customerColumnNormal: ColumnField = {
  value: nameOf<BaseLogo>((x) => x.customer),
  canSortBy: true,
  displayValue: "Customer",
  weight: 1.3,
};

// Vendor column (only for Admin role)
const vendorColumn: ColumnField = {
  value: "vendor",
  canSortBy: true,
  displayValue: "Vendor",
  weight: 1,
};

// Price column
const priceColumn: ColumnField = {
  value: "priceSort",
  canSortBy: false,
  displayValue: "Logo Price",
  weight: 1,
};

// Remaining columns after price for normal users
const remainingLogoColumnsNormal: ColumnField[] = [
  {
    value: "product.storagecount",
    canSortBy: false,
    displayValue: "In Storage",
    weight: 0.7,
  },
  {
    value: nameOf<BaseLogo>((x) => x.createdAtUtc),
    canSortBy: true,
    displayValue: "Creation Date",
    weight: 1,
  },
  {
    value: "stateFilter",
    canSortBy: true,
    displayValue: "Status",
    weight: 1,
  },
  {
    value: "actions",
    canSortBy: false,
    displayValue: "Action",
    weight: 1.2,
  },
];

// Remaining columns after price for Admin users
const remainingLogoColumnsAdmin: ColumnField[] = [
  {
    value: "product.storagecount",
    canSortBy: false,
    displayValue: "In Storage",
    weight: 0.7,
  },
  {
    value: nameOf<BaseLogo>((x) => x.createdAtUtc),
    canSortBy: true,
    displayValue: "Creation Date",
    weight: 1,
  },
  {
    value: "stateFilter",
    canSortBy: true,
    displayValue: "Status",
    weight: 1,
  },
  {
    value: "actions",
    canSortBy: false,
    displayValue: "Action",
    weight: 1.2,
  },
];

// Function to get logo columns based on price visibility and admin role
export const getLogoColumnsConfig = (
  hidePrices: boolean,
  showCustomerPrices: boolean = false,
  showVendorColumn: boolean = false,
  isAdmin: boolean = false
): ColumnField[] => {
  let displayValue = "Logo Price";

  if (hidePrices) {
    displayValue = "";
  } else if (showCustomerPrices) {
    displayValue = "Customer Price";
  }

  const priceColumnWithVisibility: ColumnField = {
    ...priceColumn,
    displayValue,
  };

  // Use appropriate column configurations based on admin status
  const baseColumns = isAdmin ? baseLogoColumnsAdmin : baseLogoColumnsNormal;
  const customerColumn = isAdmin ? customerColumnAdmin : customerColumnNormal;
  const remainingColumns = isAdmin
    ? remainingLogoColumnsAdmin
    : remainingLogoColumnsNormal;

  const columns = [...baseColumns];

  // Add vendor column before customer column if requested (for Admin role)
  if (showVendorColumn) {
    columns.push(vendorColumn);
  }

  // Always add customer column
  columns.push(customerColumn);

  columns.push(priceColumnWithVisibility, ...remainingColumns);

  return columns;
};

// Legacy export for backward compatibility
export const logoColumnsConfig: ColumnField[] = getLogoColumnsConfig(
  false,
  false,
  false,
  false
);
